import { useState, useCallback, useEffect, useMemo } from 'react';
import { customersAPI, productsAPI, dashboardAPI, salesAPI } from '../utils/api';
import { useFinancialSummaryObserver } from './useFinancialSummaryObserver';

const useDashboardData = (userRole, showError) => {
    // Ana dashboard state'leri
    const [stats, setStats] = useState({
        customers: {
            total: 0,
            newThisPeriod: 0,
            active: 0
        },
        products: {
            total: 0,
            lowStock: 0,
            outOfStock: 0,
            soldInPeriod: 0,
            newInPeriod: 0
        },
        sales: {
            period: 0,
            quantity: 0,
            total: 0,
            growth: 0
        }
    });

    // Admin dashboard için yeni state'ler
    const [dashboardData, setDashboardData] = useState({
        dailyCashFlow: {
            income: 0,
            expenses: 0,
            netFlow: 0,
            transactions: [],
            cashIncome: 0,
            cashPaymentsIncome: 0,
            cashSalesIncome: 0,
            cashExpenses: 0,
            manualCashIncome: 0,
            manualCashExpenses: 0,
            serviceExpenses: 0
        },
        productProfits: {
            totalProfit: 0,
            profitMargin: 0,
            products: []
        },
        revenue: {
            daily: 0,
            weekly: 0,
            monthly: 0,
            growth: 0
        },
        expenses: {
            daily: 0,
            weekly: 0,
            monthly: 0,
            categories: []
        },
        systemFlows: {
            sales: [],
            payments: [],
            transfers: [],
            returns: []
        },
        expenseData: [],
        financialSummary: {
            daily: { totalIncome: 0, totalExpense: 0, netAmount: 0 },
            weekly: { totalIncome: 0, totalExpense: 0, netAmount: 0 },
            monthly: { totalIncome: 0, totalExpense: 0, netAmount: 0 },
            yearly: { totalIncome: 0, totalExpense: 0, netAmount: 0 }
        }
    });

    // Elle beslenen servis verileri
    const [serviceData, setServiceData] = useState({
        income: [],
        expenses: []
    });

    const [loading, setLoading] = useState(true);

    // Admin dashboard için özel veri çekme fonksiyonu
    const fetchAdminDashboardData = useCallback(async (startDate, endDate) => {
        try {
            const dashboardResponse = await dashboardAPI.getDashboardSummary({
                startDate: startDate,
                endDate: endDate
            });
            const dashboardSummary = dashboardResponse.data;
            setDashboardData({
                dailyCashFlow: {
                    income: dashboardSummary.dailyCashFlow.income,
                    expenses: dashboardSummary.dailyCashFlow.expenses,
                    netFlow: dashboardSummary.dailyCashFlow.netFlow,
                    transactions: dashboardSummary.dailyCashFlow.transactions,
                    cashIncome: dashboardSummary.dailyCashFlow.cashIncome,
                    cashPaymentsIncome: dashboardSummary.dailyCashFlow.cashPaymentsIncome,
                    cashSalesIncome: dashboardSummary.dailyCashFlow.cashSalesIncome,
                    cashExpenses: dashboardSummary.dailyCashFlow.cashExpenses,
                    manualCashIncome: dashboardSummary.dailyCashFlow.manualCashIncome,
                    manualCashExpenses: dashboardSummary.dailyCashFlow.manualCashExpenses,
                    serviceExpenses: dashboardSummary.dailyCashFlow.serviceExpenses,
                },
                carSalesFlow: dashboardSummary.carSalesFlow,
                salesSummary: {
                    totalSalesAmount: dashboardSummary.salesSummary?.totalSalesAmount || 0,
                    totalCustomerBalance: dashboardSummary.salesSummary?.totalCustomerBalance || 0
                },
                productProfits: {
                    totalProfit: dashboardSummary.productProfits.totalProfit,
                    profitMargin: dashboardSummary.productProfits.profitMargin,
                    products: dashboardSummary.productProfits.products
                },
                revenue: {
                    daily: dashboardSummary.revenue.daily,
                    weekly: dashboardSummary.revenue.weekly,
                    monthly: dashboardSummary.revenue.monthly,
                    growth: dashboardSummary.revenue.growth
                },
                expenses: {
                    daily: dashboardSummary.expenses.daily,
                    weekly: dashboardSummary.expenses.weekly,
                    monthly: dashboardSummary.expenses.monthly,
                    categories: dashboardSummary.expenses.categories
                },
                systemFlows: {
                    sales: dashboardSummary.systemFlows.sales,
                    payments: dashboardSummary.systemFlows.payments,
                    transfers: dashboardSummary.systemFlows.transfers,
                    returns: dashboardSummary.systemFlows.returns
                },
                expenseData: dashboardSummary.expenseData || []
            });

            // Fetch financial summary data from daily_financial_summary table
            try {
                // Get all periods in parallel for better performance (including today's summary)
                const [dailySummary, weeklySummary, monthlySummary, yearlySummary, todaySummary] = await Promise.all([
                    dashboardAPI.getFinancialSummaryByPeriod('daily'),
                    dashboardAPI.getFinancialSummaryByPeriod('weekly'),
                    dashboardAPI.getFinancialSummaryByPeriod('monthly'),
                    dashboardAPI.getFinancialSummaryByPeriod('yearly'),
                    dashboardAPI.getTodayFinancialSummary()
                ]);

                setDashboardData(prevData => ({
                    ...prevData,
                    financialSummary: {
                        daily: {
                            totalIncome: dailySummary.data.totalIncome,
                            totalExpense: dailySummary.data.totalExpense,
                            netAmount: dailySummary.data.netAmount,
                            dailyData: dailySummary.data.dailyData || [],
                            today: todaySummary.data
                        },
                        weekly: {
                            totalIncome: weeklySummary.data.totalIncome,
                            totalExpense: weeklySummary.data.totalExpense,
                            netAmount: weeklySummary.data.netAmount,
                            dailyData: weeklySummary.data.dailyData || []
                        },
                        monthly: {
                            totalIncome: monthlySummary.data.totalIncome,
                            totalExpense: monthlySummary.data.totalExpense,
                            netAmount: monthlySummary.data.netAmount,
                            dailyData: monthlySummary.data.dailyData || []
                        },
                        yearly: {
                            totalIncome: yearlySummary.data.totalIncome,
                            totalExpense: yearlySummary.data.totalExpense,
                            netAmount: yearlySummary.data.netAmount,
                            dailyData: yearlySummary.data.dailyData || []
                        }
                    }
                }));
            } catch (summaryError) {
                console.error('Financial summary error:', summaryError);
                // Don't show error to user, just log it
            }

            // Directly use serviceData from dashboardSummary
            const apiServiceData = dashboardSummary.serviceData || { income: [], expenses: [] };

            setServiceData({
                income: (apiServiceData.income || []).map(item => ({
                    id: item.id,
                    name: item.name || '',
                    amount: parseFloat(item.amount || 0)
                })),
                expenses: (apiServiceData.expenses || []).map(item => ({
                    id: item.id,
                    name: item.name || '',
                    amount: parseFloat(item.amount || 0)
                }))
            });
        } catch (error) {
            showError('Admin dashboard verileri yüklenemedi: ' + (error.message || 'Bilinmeyen hata'));
            // Also set empty state to prevent undefined errors
            setDashboardData({
                dailyCashFlow: {
                    income: 0,
                    expenses: 0,
                    netFlow: 0,
                    transactions: [],
                    cashIncome: 0,
                    cashPaymentsIncome: 0,
                    cashSalesIncome: 0,
                    cashExpenses: 0,
                    manualCashIncome: 0,
                    manualCashExpenses: 0,
                    serviceExpenses: 0
                },
                productProfits: { totalProfit: 0, profitMargin: 0, products: [] },
                revenue: { daily: 0, weekly: 0, monthly: 0, growth: 0 },
                expenses: { daily: 0, weekly: 0, monthly: 0, categories: [] },
                systemFlows: { sales: [], payments: [], transfers: [], returns: [] },
                expenseData: [],
                financialSummary: {
                    daily: { totalIncome: 0, totalExpense: 0, netAmount: 0, dailyData: [] },
                    weekly: { totalIncome: 0, totalExpense: 0, netAmount: 0, dailyData: [] },
                    monthly: { totalIncome: 0, totalExpense: 0, netAmount: 0, dailyData: [] },
                    yearly: { totalIncome: 0, totalExpense: 0, netAmount: 0, dailyData: [] }
                }
            });
            setServiceData({ income: [], expenses: [] });
        }
    }, [showError]);

    const fetchDashboardData = useCallback(async (startDate, endDate) => {
        try {
            setLoading(true);

            // Fetch all stats in parallel for faster loading
            const [customerStats, lowStockProducts, salesStats] = await Promise.all([
                customersAPI.getStats({
                    startDate: startDate,
                    endDate: endDate
                }).catch(error => {
                    console.error('Customer stats error:', error);
                    return { data: { totalCustomers: 0, newThisMonth: 0, activeCustomers: 0 } };
                }),
                productsAPI.getProductStats({
                    startDate: startDate,
                    endDate: endDate
                }).catch(error => {
                    console.error('Product stats error:', error);
                    return { data: { products: [] } };
                }),
                salesAPI.getSalesStats({
                    startDate: startDate,
                    endDate: endDate
                }).catch(error => {
                    console.error('Sales stats error:', error);
                    return { data: { todaySales: 0, monthSales: 0, totalSales: 0 } };
                })
            ]);

            // Update stats immediately
            const newStats = {
                customers: {
                    total: customerStats.data.totalCustomers || 0,
                    newThisPeriod: customerStats.data.newThisPeriod || 0,
                    active: customerStats.data.activeCustomers || 0,
                    processedToday: customerStats.data.processedCustomersToday || customerStats.data.activeCustomers || 0
                },
                products: {
                    total: lowStockProducts.data.total || 0,
                    lowStock: lowStockProducts.data.lowStock || 0,
                    outOfStock: lowStockProducts.data.outOfStock || 0,
                    soldInPeriod: lowStockProducts.data.soldInPeriod || 0,
                    newInPeriod: lowStockProducts.data.newInPeriod || 0
                },
                sales: {
                    period: salesStats.data.periodSales || 0,
                    quantity: salesStats.data.periodQuantity || 0,
                    total: salesStats.data.totalSales || 0,
                    growth: salesStats.data.growthRate || 0
                }
            };

            setStats(prevStats => ({
                ...prevStats,
                ...newStats
            }));

            // Admin için ek veri çekme (don't await - load in background)
            if (userRole === 'admin') {
                // Start admin data fetch but don't block initial render
                fetchAdminDashboardData(startDate, endDate);
            }

        } catch (error) {
            console.error('Dashboard data error:', error);
        } finally {
            setLoading(false);
        }
    }, [userRole, fetchAdminDashboardData]);

    // Auto-fetch data on mount for admin role
    useEffect(() => {
        const today = new Date();
        const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        if (userRole === 'admin') {
            fetchAdminDashboardData(startDate, endDate);
        }
    }, [userRole, fetchAdminDashboardData]);

    // Manual save financial summary for a date (can be called from components)
    const saveFinancialSummaryForDate = useCallback(async (date) => {
        try {
            await dashboardAPI.saveFinancialSummary(date);
            // Optionally refresh the data after saving
            const today = new Date();
            const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
            const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            if (userRole === 'admin') {
                await fetchAdminDashboardData(startDate, endDate);
            }
            return { success: true };
        } catch (error) {
            console.error('Failed to save financial summary:', error);
            showError('Finansal özet kaydedilemedi: ' + (error.message || 'Bilinmeyen hata'));
            return { success: false, error };
        }
    }, [userRole, fetchAdminDashboardData, showError]);

    // Auto-update financial summary when financial data changes (for admin only)
    const todayDate = useMemo(() => new Date(), []);
    const financialObserver = useFinancialSummaryObserver(todayDate);

    // Refresh dashboard data when observer automatically updates financial summary
    useEffect(() => {
        if (userRole === 'admin' && financialObserver.isActive) {
            const startDate = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);
            const endDate = new Date(todayDate.getFullYear(), todayDate.getMonth() + 1, 0);
            fetchAdminDashboardData(startDate, endDate);
        }
    }, [userRole, fetchAdminDashboardData, financialObserver.isActive, todayDate]);

    return {
        stats,
        dashboardData,
        serviceData,
        loading,
        fetchDashboardData,
        fetchAdminDashboardData,
        saveFinancialSummaryForDate
    };
};

export default useDashboardData;
